import { Injectable, inject, signal } from '@angular/core';
import { ApiClientService } from '../core/api/api-client.service';
import { SuiteGroupStore } from '../stores/suite-group.store';
import { SuiteStore } from '../stores/suite.store';
import { SuiteGroupClusterStore } from '../stores/suite-group-cluster.store';
import { SuiteGroupClusterTimingStore } from '../stores/suite-group-cluster-timing.store';
import { AppStore } from '../stores/app.store';
import { UiStore } from '../stores/ui.store';
import {
  SuiteGroupModel,
  GetSuiteGroupsResponse,
  GetSuiteGroupResponse,
  StoreSuiteGroupResponse,
} from '../shared/models/suite-group.model';
import { SuiteModel } from '../shared/models/suite.model';
import { SuiteGroupClusterModel } from '../shared/models/suite-group-cluster.model';
import { SuiteGroupClusterTimingModel } from '../shared/models/suite-group-cluster-timing.model';
import { ApiResponse, EMPTY_GUID } from '../shared/models/types';
import { SignalRService } from '../core/signalr/signalr.service';
import { SignalREvents } from '../core/signalr/signalr.constants';

export interface SuiteAssignment {
  suiteId: Guid;
  suiteGroupId: Guid | null;
  suiteGroupSortOrder: number | null;
}

@Injectable({ providedIn: 'root' })
export class SuiteGroupManagerService {
  private readonly apiClient = inject(ApiClientService);
  private readonly suiteGroupStore = inject(SuiteGroupStore);
  private readonly suiteStore = inject(SuiteStore);
  private readonly suiteGroupClusterStore = inject(SuiteGroupClusterStore);
  private readonly suiteGroupClusterTimingStore = inject(SuiteGroupClusterTimingStore);
  private readonly appStore = inject(AppStore);
  private readonly uiStore = inject(UiStore);
  private readonly signalR = inject(SignalRService);
  private readonly baseUrl = 'admin/suite-groups';

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();
  readonly suiteGroups = this.suiteGroupStore.suiteGroups;
  readonly selectedSuiteGroup = this.suiteGroupStore.selectedSuiteGroup;

  subscribeToSuiteEvents(): void {
    this.signalR.on<{ suites: SuiteModel[] }>(SignalREvents.FacilitySuites, (payload) => {
      this.suiteStore.setSuites(payload.suites ?? []);
    });

    this.signalR.on<{ suiteGroups: SuiteGroupModel[] }>(SignalREvents.FacilitySuiteGroups, (payload) => {
      this.suiteGroupStore.setSuiteGroups(payload.suiteGroups ?? []);
    });

    this.signalR.on<{ suiteGroupClusters: SuiteGroupClusterModel[] }>(
      SignalREvents.FacilitySuiteGroupClusters,
      (payload) => {
        this.suiteGroupClusterStore.setSuiteGroupClusters(payload.suiteGroupClusters ?? []);
      }
    );

    this.signalR.on<{ suiteGroupClusterTimings: SuiteGroupClusterTimingModel[] }>(
      SignalREvents.FacilitySuiteGroupClusterTimings,
      (payload) => {
        this.suiteGroupClusterTimingStore.setTimings(payload.suiteGroupClusterTimings ?? []);
      }
    );
  }

  unsubscribeFromSuiteEvents(): void {
    this.signalR.off(SignalREvents.FacilitySuites);
    this.signalR.off(SignalREvents.FacilitySuiteGroups);
    this.signalR.off(SignalREvents.FacilitySuiteGroupClusters);
    this.signalR.off(SignalREvents.FacilitySuiteGroupClusterTimings);
  }

  async loadSuiteGroups(): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    if (!facilityId) {
      this.uiStore.showToast('Please select a facility first.', 'error');
      return;
    }

    this._isLoading.set(true);
    try {
      const result = await this.apiClient.get<GetSuiteGroupsResponse>(this.baseUrl, { facilityId });

      if (result.success) {
        this.suiteGroupStore.setSuiteGroups(result.suiteGroups);
      } else {
        this.uiStore.showToast(result.messages?.[0] ?? 'Failed to load location groups.', 'error');
      }
    } finally {
      this._isLoading.set(false);
    }
  }

  async loadSuiteGroup(id: Guid): Promise<void> {
    const result = await this.apiClient.get<GetSuiteGroupResponse>(`${this.baseUrl}/${id}`);

    if (result.success) {
      this.suiteGroupStore.setSelected(result.suiteGroup);
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to load location group.', 'error');
    }
  }

  async saveSuiteGroup(suiteGroup: SuiteGroupModel): Promise<StoreSuiteGroupResponse> {
    const isNew = suiteGroup.id === null || suiteGroup.id === EMPTY_GUID;
    const payload: SuiteGroupModel = { ...suiteGroup, id: isNew ? EMPTY_GUID : suiteGroup.id };

    const result = await this.apiClient.post<SuiteGroupModel, StoreSuiteGroupResponse>(
      this.baseUrl,
      payload
    );

    if (result.success) {
      if (isNew) {
        this.suiteGroupStore.addSuiteGroup(result.suiteGroup);
      } else {
        this.suiteGroupStore.updateSuiteGroup(result.suiteGroup);
      }
      this.uiStore.showToast('Location group saved.', 'success');
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to save location group.', 'error');
    }

    return result;
  }

  async deleteSuiteGroup(id: Guid, facilityId: Guid): Promise<boolean> {
    const result = await this.apiClient.delete<ApiResponse>(`${this.baseUrl}/${id}?facilityId=${facilityId}`);

    if (result.success) {
      this.suiteGroupStore.removeSuiteGroup(id);
      this.uiStore.showToast('Location group deleted.', 'success');
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to delete group.', 'error');
    }

    return result.success;
  }

  async assignSuites(facilityId: Guid, assignments: SuiteAssignment[]): Promise<boolean> {
    const result = await this.apiClient.post<{ facilityId: Guid; assignments: SuiteAssignment[] }, ApiResponse>(
      `${this.baseUrl}/assignments`,
      { facilityId, assignments }
    );

    if (result.success) {
      this.uiStore.showToast('Suite assignments saved.', 'success');
    } else {
      this.uiStore.showToast(result.messages?.[0] ?? 'Failed to save assignments.', 'error');
    }

    return result.success;
  }
}
